import { Announced } from '@fluentui/react';
import {
    ArrowCircleRight20Regular,
    ArrowClockwise20Regular,
    ArrowReset20Regular,
    Camera20Regular,
    Eraser20Regular,
    LaptopDismiss20Regular,
    New20Regular,
    PlayCircle20Regular,
    PlayCircleHint20Regular,
    PlugConnectedCheckmark20Regular,
    WeatherMoon20Regular,
    WrenchScrewdriver20Regular,
} from '@fluentui/react-icons';
import { defineMessages } from '@formatjs/intl';
import React from 'react';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import CircleHintDismiss20Regular from '../../../icons/circle-hint-dismiss_20_regular';
import { DevBoxState } from '../../../redux/store/dev-box-state';
import { isDayAfterNextDay, isNextDay } from '../../../utilities/time';
import {
    MetadataItemViewModel,
    ShimmeredMetadataItemViewModel,
    ShimmeredMetadataItemWidth,
} from '../../common/metadata/models';
import { getScheduleMetadataMessageValues } from '../dev-box-metadata/selectors';
import { sharedMessages } from './messages';

const messages = defineMessages({
    creatingAriaLabel: {
        id: 'DevBoxStateMessage_Creating_AriaLabel',
        defaultMessage: 'Creating your dev box',
        description: 'Aria label for tracking progress of dev box creation in dev box card',
    },
    deletingAriaLabel: {
        id: 'DevBoxStateMessage_Deleting_AriaLabel',
        defaultMessage: 'Deleting your dev box',
        description: 'Aria label for tracking progress of dev box deletion in dev box card',
    },
    runningAriaLabel: {
        id: 'DevBoxStateMessage_Running_AriaLabel',
        defaultMessage: 'Your dev box is running',
        description: 'Aria label for running dev box state in dev box card',
    },
    startingAriaLabel: {
        id: 'DevBoxStateMessage_Starting_AriaLabel',
        defaultMessage: 'Starting your dev box',
        description: 'Aria label for tracking progress of dev box starting in dev box card',
    },
    stoppingAriaLabel: {
        id: 'DevBoxStateMessage_Stopping_AriaLabel',
        defaultMessage: 'Shutting down your dev box',
        description: 'Aria label for tracking progress of dev box stopping in dev box card',
    },
    stoppedAriaLabel: {
        id: 'DevBoxStateMessage_Stopped_AriaLabel',
        defaultMessage: 'Your dev box is stopped',
        description: 'Aria label for stopped dev box state in dev box card',
    },
    hibernatedAriaLabel: {
        id: 'DevBoxStateMessage_Hibernated_AriaLabel',
        defaultMessage: 'Your dev box is hibernated',
        description: 'Aria label for hibernated dev box state in dev box card',
    },
    hibernatingAriaLabel: {
        id: 'DevBoxStateMessage_Hibernating_AriaLabel',
        defaultMessage: 'Hibernating your dev box',
        description: 'Aria label for tracking progress of dev box hibernating in dev box card',
    },
    resumingAriaLabel: {
        id: 'DevBoxStateMessage_Resuming_AriaLabel',
        defaultMessage: 'Resuming your dev box',
        description: 'Aria label for tracking progress of dev box resuming in dev box card',
    },
    restartingAriaLabel: {
        id: 'DevBoxStateMessage_Restarting_AriaLabel',
        defaultMessage: 'Restarting your dev box',
        description: 'Aria label for tracking progress of dev box restarting in dev box card',
    },
    repairedAriaLabel: {
        id: 'DevBoxStateMessage_Repaired_AriaLabel',
        defaultMessage: 'Your dev box has been repaired',
        description: 'Aria label for repaired dev box state in dev box card',
    },
    restoringAriaLabel: {
        id: 'DevBoxStateMessage_Restoring_AriaLabel',
        defaultMessage: 'Restoring your dev box',
        description: 'Aria label for tracking progress of dev box restoration in dev box card',
    },
    restoredAriaLabel: {
        id: 'DevBoxStateMessage_Restored_AriaLabel',
        defaultMessage: 'Your dev box has been restored',
        description: 'Aria label for restored dev box state in dev box card',
    },
    snapshotCapturingAriaLabel: {
        id: 'DevBoxStateMessage_SnapshotCapturing_AriaLabel',
        defaultMessage: 'Capturing snapshot of your dev box',
        description: 'Aria label for tracking progress of dev box snapshot capturing in dev box card',
    },
    snapshotCapturedAriaLabel: {
        id: 'DevBoxStateMessage_SnapshotCaptured_AriaLabel',
        defaultMessage: 'A snapshot of your dev box has been captured',
        description: 'Aria label for snapshot captured dev box state in dev box card',
    },
    aligningAriaLabel: {
        id: 'DevBoxStateMessage_Moving_AriaLabel',
        defaultMessage: 'Moving your dev box',
        description: 'Aria label for tracking progress of dev box moving in dev box card',
    },
    hibernateDayOfWeekMessageText: {
        id: 'ScheduleMetadata_HibernateDayOfWeekMessage_Text',
        defaultMessage: 'Hibernates at {displayTime} {dayOfTheWeek}',
        description: 'Information indicating when this dev box will hibernate. Do not localize {displayTime}.',
    },
    hibernateTomorrowMessageText: {
        id: 'ScheduleMetadata_HibernateTomorrowMessage_Text',
        defaultMessage: 'Hibernates at {displayTime} tomorrow',
        description: 'Information indicating when this dev box will hibernate. Do not localize {displayTime}.',
    },
    hibernateMessageText: {
        id: 'ScheduleMetadata_HibernateMessage_Text',
        defaultMessage: 'Hibernates at {displayTime}',
        description: 'Information indicating when this dev box will hibernate. Do not localize {displayTime}.',
    },
    shutDownDayOfWeekMessageText: {
        id: 'ScheduleMetadata_ShutDownDayOfWeekMessage_Text',
        defaultMessage: 'Shuts down at {displayTime} {dayOfTheWeek}',
        description: 'Information indicating when this dev box will shutdown. Do not localize {displayTime}.',
    },
    shutDownTomorrowMessageText: {
        id: 'ScheduleMetadata_ShutdownTomorrowMessage_Text',
        defaultMessage: 'Shuts down at {displayTime} tomorrow',
        description: 'Information indicating when this dev box will shutdown. Do not localize {displayTime}.',
    },
    shutDownMessageText: {
        id: 'ScheduleMetadata_ShutDownMessage_Text',
        defaultMessage: 'Shuts down at {displayTime}',
        description: 'Information indicating when this dev box will shutdown. Do not localize {displayTime}.',
    },
});

interface StateMetadataViewModel extends Omit<MetadataItemViewModel, 'description'> {
    description: MessageDescriptor;
}

interface ScheduleMetadataProps {
    scheduledTime: Date;
    hasHibernateSupport: boolean;
    locale: string;
}

interface DevBoxStateAnnouncementProps {
    isInProgress: boolean;
    isReady: boolean;
    message?: string;
    state: DevBoxState;
    suppressAnnouncement?: boolean;
}

export const getDevBoxStateMessage = (state: DevBoxState): StateMetadataViewModel | undefined => {
    switch (state) {
        case DevBoxState.Creating:
            return {
                description: messages.creatingAriaLabel,
                icon: <New20Regular />,
                key: 'devbox-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="DevBoxStateMessage_CreatingMessage_Text"
                        defaultMessage="Creating..."
                        description="Message informing user that dev box creation is in progress"
                    />
                ),
            };

        case DevBoxState.Deleting:
            return {
                description: messages.deletingAriaLabel,
                icon: <Eraser20Regular />,
                key: 'devbox-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="DevBoxStateMessage_DeletingState_Text"
                        defaultMessage="Deleting..."
                        description="Text informing user that dev box is in the process of being deleted."
                    />
                ),
            };
        case DevBoxState.Running:
            return {
                description: messages.runningAriaLabel,
                icon: <PlayCircle20Regular />,
                key: 'devbox-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="DevBoxStateMessage_RunningState_Text"
                        defaultMessage="Running"
                        description="Text informing user that dev box is running."
                    />
                ),
            };
        case DevBoxState.Starting:
            return {
                description: messages.startingAriaLabel,
                icon: <PlayCircleHint20Regular />,
                key: 'devbox-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="DevBoxStateMessage_StartingState_Text"
                        defaultMessage="Starting..."
                        description="Text informing user that dev box is starting."
                    />
                ),
            };
        case DevBoxState.Stopping:
            return {
                description: messages.stoppingAriaLabel,
                icon: <CircleHintDismiss20Regular />,
                key: 'devbox-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="DevBoxStateMessage_StoppingState_Text"
                        defaultMessage="Stopping..."
                        description="Text informing user that dev box is in the process of being stopped."
                    />
                ),
            };
        case DevBoxState.Stopped:
            return {
                description: messages.stoppedAriaLabel,
                icon: <LaptopDismiss20Regular />,
                key: 'devbox-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="DevBoxStateMessage_StoppedState_Text"
                        defaultMessage="Stopped"
                        description="Text informing user that dev box is stopped."
                    />
                ),
            };
        case DevBoxState.Repaired:
            return {
                description: messages.repairedAriaLabel,
                icon: <PlugConnectedCheckmark20Regular />,
                key: 'devbox-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="DevBoxStateMessage_RepairedState_Text"
                        defaultMessage="Repaired"
                        description="Text informing user that dev box has been repaired."
                    />
                ),
            };
        case DevBoxState.Hibernated:
            return {
                description: messages.hibernatedAriaLabel,
                icon: <WeatherMoon20Regular />,
                key: 'devbox-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="DevBoxStateMessage_HibernatedState_Text"
                        defaultMessage="Hibernated"
                        description="Text informing user that the dev box is hibernated."
                    />
                ),
            };
        case DevBoxState.Hibernating:
            return {
                description: messages.hibernatingAriaLabel,
                icon: <WeatherMoon20Regular />,
                key: 'devbox-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="DevBoxStateMessage_HibernatingState_Text"
                        defaultMessage="Hibernating..."
                        description="Text informing user that the dev box is in the process of being hibernated."
                    />
                ),
            };
        case DevBoxState.Resuming:
            return {
                description: messages.resumingAriaLabel,
                icon: <PlayCircleHint20Regular />,
                key: 'devbox-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="DevBoxStateMessage_ResumingState_Text"
                        defaultMessage="Resuming..."
                        description="Text informing user that the dev box is in the process of being resumed from hibernate."
                    />
                ),
            };
        case DevBoxState.Restarting:
            return {
                description: messages.restartingAriaLabel,
                icon: <ArrowClockwise20Regular />,
                key: 'devbox-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="DevBoxStateMessage_RestartingState_Text"
                        defaultMessage="Restarting..."
                        description="Text informing user that the dev box is in the process of being restarted."
                    />
                ),
            };
        case DevBoxState.Repairing:
            return {
                description: sharedMessages.repairOperationInProgress,
                icon: <WrenchScrewdriver20Regular />,
                key: 'devbox-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="DevBoxStateMessage_RepairingState_Text"
                        defaultMessage="Repairing..."
                        description="Text informing user that the dev box is in the process of being repaired."
                    />
                ),
            };
        case DevBoxState.Restoring:
            return {
                description: messages.restoringAriaLabel,
                icon: <ArrowReset20Regular />,
                key: 'devbox-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="DevBoxStateMessage_RestoringState_Text"
                        defaultMessage="Restoring..."
                        description="Text informing user that the dev box is in the process of being restored."
                    />
                ),
            };
        case DevBoxState.Restored:
            return {
                description: messages.restoredAriaLabel,
                icon: <PlugConnectedCheckmark20Regular />,
                key: 'devbox-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="DevBoxStateMessage_RestoredState_Text"
                        defaultMessage="Restored"
                        description="Text informing user that the dev box has been restored."
                    />
                ),
            };
        case DevBoxState.SnapshotCapturing:
            return {
                description: messages.snapshotCapturingAriaLabel,
                icon: <Camera20Regular />,
                key: 'devbox-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="DevBoxStateMessage_SnapshotCapturingState_Text"
                        defaultMessage="Capturing snapshot..."
                        description="Text informing user that a snapshot of the dev box is being captured."
                    />
                ),
            };
        case DevBoxState.SnapshotCaptured:
            return {
                description: messages.snapshotCapturedAriaLabel,
                icon: <Camera20Regular />,
                key: 'devbox-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="DevBoxStateMessage_SnapshotCapturedState_Text"
                        defaultMessage="Snapshot captured"
                        description="Text informing user that a snapshot of the dev box has been captured."
                    />
                ),
            };
        case DevBoxState.Aligning:
            return {
                description: messages.aligningAriaLabel,
                icon: <ArrowCircleRight20Regular />,
                key: 'devbox-status',
                primary: false,
                value: (
                    <FormattedMessage
                        id="DevBoxStateMessage_MovingState_Text"
                        defaultMessage="Moving..."
                        description="Text informing user that the dev box is in the process of being moved."
                    />
                ),
            };
        // TODO: Unknown states are not expected, but showing another state is also not accurate.
        // For now the state text is empty if state is unknown.
        default:
            return undefined;
    }
};

export const getDevBoxStateMetadata = (
    state: DevBoxState,
    formatMessage: (message: MessageDescriptor) => string
): MetadataItemViewModel | undefined => {
    const stateMessage = getDevBoxStateMessage(state);

    return stateMessage
        ? {
              ...stateMessage,
              description: formatMessage(stateMessage.description),
          }
        : undefined;
};

export const DevBoxStateAnnouncement: React.FC<DevBoxStateAnnouncementProps> = (
    props: DevBoxStateAnnouncementProps
) => {
    const { isInProgress, isReady, message, state, suppressAnnouncement = false } = props;
    const previousState = React.useRef(state);
    const previousIsInProgress = React.useRef(isInProgress);
    const previousIsReady = React.useRef(isReady);
    const [announcement, setAnnouncement] = React.useState<{ key: number; message: string }>();

    React.useEffect(() => {
        const stateChanged = previousState.current !== state;
        const shouldAnnounce =
            !suppressAnnouncement &&
            previousIsReady.current &&
            isReady &&
            stateChanged &&
            (isInProgress || previousIsInProgress.current);

        previousState.current = state;
        previousIsInProgress.current = isInProgress;
        previousIsReady.current = isReady;

        if (shouldAnnounce && message) {
            setAnnouncement((previousAnnouncement) => ({
                key: (previousAnnouncement?.key ?? 0) + 1,
                message,
            }));
        }
    }, [isInProgress, isReady, message, state, suppressAnnouncement]);

    return announcement ? <Announced key={announcement.key} message={announcement.message} /> : <></>;
};

export const ScheduleMetadata: React.FC<ScheduleMetadataProps> = React.memo((props: ScheduleMetadataProps) => {
    const { scheduledTime, locale, hasHibernateSupport } = props;

    const currentTime = new Date();

    const isTomorrow = isNextDay(currentTime, scheduledTime);

    const isDayAfterNext = isDayAfterNextDay(currentTime, scheduledTime);

    // Memoized data
    const values = React.useMemo(
        () => getScheduleMetadataMessageValues(scheduledTime, locale),
        [scheduledTime, locale]
    );

    if (isDayAfterNext) {
        return hasHibernateSupport ? (
            <FormattedMessage {...messages.hibernateDayOfWeekMessageText} values={values} />
        ) : (
            <FormattedMessage {...messages.shutDownDayOfWeekMessageText} values={values} />
        );
    }

    if (isTomorrow) {
        return hasHibernateSupport ? (
            <FormattedMessage {...messages.hibernateTomorrowMessageText} values={values} />
        ) : (
            <FormattedMessage {...messages.shutDownTomorrowMessageText} values={values} />
        );
    }

    return hasHibernateSupport ? (
        <FormattedMessage {...messages.hibernateMessageText} values={values} />
    ) : (
        <FormattedMessage {...messages.shutDownMessageText} values={values} />
    );
});

export const shimmeredDevBoxMetadata: ShimmeredMetadataItemViewModel[] = [
    { key: 'devbox-ostype-shimmer', width: ShimmeredMetadataItemWidth.Small },
    { key: 'devbox-schedule-shimmer', width: ShimmeredMetadataItemWidth.Medium },
    { key: 'devbox-state-shimmer', width: ShimmeredMetadataItemWidth.XSmall },
];
